// src/utils/httpClient.js
import axios from "axios";
import { ApacuanaAPIError } from "../errors/index";
import { getConfig } from "../config/index";

let axiosInstance;

/**
 * Inicializa el cliente HTTP (Axios) para todas las peticiones del SDK.
 * @throws {Error} Si la configuración es incompleta.
 */
export const initHttpClient = () => {
  const config = getConfig();

  if (
    !config.apiUrl ||
    !config.secretKey ||
    !config.apiKey ||
    !config.verificationId
  ) {
    throw new Error(
      "HttpClient: Configuración de inicialización incompleta. Llama a apacuana.init() primero."
    );
  }

  axiosInstance = axios.create({
    baseURL: config.apiUrl,
    headers: {
      "APACUANA-API-Key": config.apiKey,
      "APACUANA-SECRET-Key": config.secretKey,
      // La cabecera Authorization se deja vacía al inicio.
      // Se actualizará de forma dinámica con setAuthToken().
    },
    timeout: 30000,
  });

  // Configura los interceptores de Axios
  axiosInstance.interceptors.response.use(
    (response) => {
      // Maneja errores lógicos de la API (si el backend devuelve status 2xx con { success: false })
      if (response.data && response.data.success === false) {
        const errorMessage =
          response.data?.msg ||
          response.data?.message ||
          "Error lógico desconocido desde la API.";
        throw new ApacuanaAPIError(
          errorMessage,
          response.status,
          response.data.code || "LOGICAL_API_ERROR"
        );
      }
      return response;
    },
    (error) => {
      // Maneja errores HTTP (status 4xx, 5xx) o errores de red
      // eslint-disable-next-line no-console
      console.error(
        "[HTTP Client] Interceptor de error de Axios:",
        error.response || error.message
      );

      if (error.response) {
        const { status, data } = error.response;
        const errorMessage =
          data?.msg || data?.message || "Error desconocido desde la API.";
        throw new ApacuanaAPIError(
          errorMessage,
          status,
          data?.code || "API_ERROR"
        );
      }
      if (error.request) {
        // La petición fue hecha, pero no se recibió respuesta (red caída, CORS, timeout)
        throw new ApacuanaAPIError(
          "Error de conexión a la red, timeout o CORS.",
          0,
          "NETWORK_ERROR"
        );
      } else {
        // Algo más ocurrió al configurar la petición
        throw new ApacuanaAPIError(
          `Error desconocido en la petición: ${error.message}`,
          0,
          "UNKNOWN_REQUEST_ERROR"
        );
      }
    }
  );
};

/**
 * Establece el token de autenticación Bearer para las peticiones subsiguientes.
 * @param {string} token - El token de sesión del usuario.
 * @throws {Error} Si el token es inválido o el cliente no está inicializado.
 */
export const setAuthToken = (token) => {
  if (!axiosInstance) {
    throw new Error(
      "HttpClient no inicializado. Llama a apacuana.init() primero."
    );
  }
  if (!token || typeof token !== "string") {
    throw new Error("Token de sesión inválido.");
  }

  // Actualizamos la cabecera Authorization de la instancia de Axios.
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  // eslint-disable-next-line no-console
};

/**
 * Envía una petición HTTP a un endpoint de la API.
 * Inyecta automáticamente los parámetros de inicialización en el cuerpo de la petición.
 * @param {string} path - La ruta del endpoint de la API.
 * @param {object} [data={}] - Los datos para enviar en la petición.
 * @param {string} [method='POST'] - El método HTTP.
 * @returns {Promise<object>} Los datos de la respuesta del backend.
 * @throws {ApacuanaAPIError} Si la petición falla.
 */
export const httpRequest = async (path, data = {}, method = "POST") => {
  if (!axiosInstance) {
    throw new Error(
      "Apacuana SDK: Cliente HTTP no inicializado. " +
        "Llama a apacuana.init() primero."
    );
  }

  let dataToSend;
  const requestConfig = {};

  if (typeof FormData !== "undefined" && data instanceof FormData) {
    dataToSend = data;
  } else {
    const hasFile = Object.values(data).some(
      (value) => typeof File !== "undefined" && value instanceof File
    );
    if (hasFile) {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      dataToSend = formData;
    } else {
      dataToSend = { ...data };
    }
  }

  try {
    let response;
    switch (method.toUpperCase()) {
      case "GET":
        response = await axiosInstance.get(path, { params: dataToSend });
        break;
      case "POST":
        response = await axiosInstance.post(path, dataToSend, requestConfig);
        break;
      case "PUT":
        response = await axiosInstance.put(path, dataToSend, requestConfig);
        break;
      case "DELETE":
        response = await axiosInstance.delete(path, {
          data: dataToSend,
          ...requestConfig,
        });
        break;
      default:
        throw new ApacuanaAPIError(
          `Método HTTP no soportado: ${method}`,
          405,
          "UNSUPPORTED_HTTP_METHOD"
        );
    }

    return response.data;
  } catch (error) {
    console.error(`[HTTP Client] Fallo en la petición a ${path}:`, error);
    throw error;
  }
};
